import { Component, Element, Event, EventEmitter, Listen, Method, Prop, State, Watch } from "@stencil/core";
import api from "../../utils/api";
import { debounce } from "../../utils/debounce";
import { GuidedTourData, GuidedTourStep } from "./types";
import { localStorageNameCompleted, triggerAttributeName } from "./constants";
import { getAutoStartEnabled, isDefined } from "./utils";
import { logger } from "../../utils/console";

@Component({
    tag: "ux-guided-tour",
    styleUrl: "guided-tour.scss",
    shadow: false
})
export class GuidedTour {
    @Element() el!: HTMLUxGuidedTourElement;

    /** The storyboard configuration */
    // eslint-disable-next-line @stencil-community/props-must-be-readonly
    @Prop() data: GuidedTourData | null = null;
    /** CSS selector used to find the data script tag in the DOM tree */
    @Prop() readonly dataScriptSelector: string;
    /** API endpoint url from which data is requested */
    @Prop() readonly endpoint: string;
    /** Manage autostart additionally to autostart via data */
    @Prop() readonly autoStart: boolean = false;
    /** Indicates if the component is used in a static context */
    @Prop() readonly staticUsage: boolean = false;
    /** Indicates if the tour state should be persisted using localStorage. Defaults to true. */
    @Prop() readonly persist: boolean = true;

    /** Flag that indicates if the guided-tour is active or not */
    @State() isActive = false;
    /** Flag that indicates that the viewport is scrolled or resized */
    @State() viewportChanging = false;
    /** The currently active guided-tour step */
    @State() currentStep: GuidedTourStep | null = null;

    private targetRect: DOMRect | null = null;
    private overlayElement: (HTMLUxGuidedTourOverlayElement & { step: GuidedTourStep | null }) | null = null;
    private currentStepIndex = -1;
    private viewportFixed = false;

    get totalSteps(): number {
        if (isDefined(this.data) && Array.isArray(this.data?.steps)) {
            return this.data.steps.length;
        }
        return 0;
    }

    @Watch("data")
    setData() {
        if (this.data === null) {
            this.loadData();
        } else {
            this.tourHash = generateHash(JSON.stringify(this.data)).toString();
            this.hasCompletedTour = this.getHasCompletedTour();
            if ((this.data.autoStart || this.autoStart) && !this.hasCompletedTour && getAutoStartEnabled(this.tourHash)) {
                this.activate();
            }
        }
    }

    @Watch("endpoint")
    setEndpoint() {
        if (typeof this.endpoint === "string") {
            this.loadData();
        }
    }

    @Watch("isActive")
    setIsActive() {
        document.removeEventListener("click", this.handleClickDoc);
        if (!this.isActive) {
            document.addEventListener("click", this.handleClickDoc);
        }
    }

    @Watch("viewportChanging")
    setViewportChanging() {
        if (this.viewportChanging) {
            if (this.overlayElement !== null) {
                this.overlayElement.targetRect = null;
            }
        }
    }

    @Watch("currentStep")
    setCurrentStep() {
        this.viewportFixed = false;
        if (this.currentStep !== null) {
            this.showCurrentStep();
        }
    }

    @Listen("scroll", { target: "window" })
    handleScroll() {
        this.viewportChanging = true;
        this.viewportFixed = false;
        if (isDefined(this.overlayElement)) {
            if (this.overlayElement !== null) {
                this.overlayElement.step = null as unknown as GuidedTourStep;
            }
        }
        this.showCurrentStepDebounced();
    }

    @Listen("resize", { target: "window" })
    handleResize() {
        this.viewportChanging = true;
        if (isDefined(this.overlayElement)) {
            if (this.overlayElement !== null) {
                this.overlayElement.step = null as unknown as GuidedTourStep;
            }
        }
        this.showCurrentStepDebounced();
    }

    /** Fired when the current step has changed */
    @Event() uxGuidedTourChange!: EventEmitter<{
        prevStep: {
            target?: string;
            targetOffset?: number;
            title?: string;
        } | null;
        nextStep: {
            target?: string;
            targetOffset?: number;
            title?: string;
        };
    }>;

    /**
     * Fired when the guided-tour is canceled
     * @event
     */
    @Event() uxGuidedTourCancel!: EventEmitter<void>;

    /**
     * Fired when the guided-tour is finished
     * @event
     */
    @Event() uxGuidedTourFinish!: EventEmitter<void>;

    /**
     * Fired when the guided-tour is started
     * @event
     */
    @Event() uxGuidedTourActivate!: EventEmitter<void>;

    /**
     * Jump to next guided-tour step
     * @returns {Promise<void>}
     */
    @Method()
    async nextStep(): Promise<void> {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data?.outro) && this.currentStep === this.data?.outro) {
            this.deactivate();
            return;
        }
        this.showStep(this.currentStepIndex + 1);
    }

    /**
     * Jump to previous guided-tour step
     * @returns {Promise<void>}
     */
    @Method()
    async prevStep(): Promise<void> {
        if (!this.isActive || this.currentStepIndex <= 0) {
            return;
        }
        this.showStep(this.currentStepIndex - 1);
    }

    /**
     * Activate the guided-tour
     * @returns {Promise<void>}
     */
    @Method()
    async activate(): Promise<void> {
        if (this.isActive || !isDefined(this.data)) {
            return;
        }
        // ToDo: move logic to storybook asap
        // deactivate other guided tours (storybook)
        if (window.parent !== null) {
            window.parent.document.querySelectorAll<HTMLUxGuidedTourElement>("ux-guided-tour").forEach((tour) => {
                if (tour !== this.el) {
                    tour.deactivate();
                }
            });
        }
        document.querySelectorAll<HTMLUxGuidedTourElement>("ux-guided-tour").forEach((tour) => {
            if (tour !== this.el) {
                tour.deactivate();
            }
        });
        this.isActive = true;
        this.uxGuidedTourActivate.emit();
        if (isDefined(this.data?.intro)) {
            this.showIntro();
        } else {
            this.showStep(0);
        }
    }

    /**
     * Deactivate the guided-tour
     * @returns {Promise<void>}
     */
    @Method()
    async deactivate(): Promise<void> {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data?.outro) && this.currentStep !== this.data?.outro) {
            this.showOutro();
            return;
        }
        this.removeOverlay();
        this.isActive = false;
        this.currentStep = null;
        this.currentStepIndex = -1;
    }

    componentWillLoad() {
        this.setData();
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.handleClickDoc);
        this.removeOverlay();
    }

    private finish() {
        logger.info("Guided tour finished");
        this.setHasCompletedTour();
        this.uxGuidedTourFinish.emit();
    }

    private onCancel() {
        logger.info("Guided tour canceled");
        this.uxGuidedTourCancel.emit();
    }

    private loadData = async () => {
        if (this.data !== null && Array.isArray(this.data.steps)) {
            this.setIsActive();
            return;
        }
        if (typeof this.endpoint === "string") {
            try {
                this.data = await api<GuidedTourData>(this.endpoint);
                this.data = this.staticUsage && this.data ? this.filterValidSteps(this.data) : this.data;
                this.setIsActive();
                return;
            } catch (e) {
                if (e instanceof Error) {
                    logger.warn(`Invalid guided-tour data received from endpoint ${e.message} `);
                }
            }
        }

        const scriptElement = document.querySelector<HTMLScriptElement>(this.dataScriptSelector);
        if (scriptElement !== null) {
            try {
                this.data = JSON.parse(scriptElement.innerText);
                this.data = this.staticUsage && this.data ? this.filterValidSteps(this.data) : this.data;
                this.setIsActive();
            } catch (e) {
                if (e instanceof Error) {
                    logger.warn(`Invalid guided-tour data ${e.message} `);
                }
            }
        }
    };

    private filterValidSteps(data: GuidedTourData): GuidedTourData {
        const validSteps = data.steps.filter((step) => {
            return step.target !== undefined && document.querySelector(step.target) !== null;
        });
        return { ...data, steps: validSteps };
    }

    private tourHash: string;
    private completedTourHashes: string[] = [];
    private hasCompletedTour = false;

    private getHasCompletedTour = () => {
        if (!this.persist) {
            return false;
        }
        const val = window.localStorage.getItem(localStorageNameCompleted);
        if (typeof val !== "string" || val.length === 0) {
            return false;
        }
        try {
            const hashes = JSON.parse(val);
            if (Array.isArray(hashes)) {
                this.completedTourHashes = hashes;
                return this.completedTourHashes.includes(this.tourHash);
            }
        } catch {
            return false;
        }
        return false;
    };

    private setHasCompletedTour = () => {
        if (!this.persist) {
            return;
        }
        if (typeof this.tourHash !== "string" || this.tourHash.length === 0 || this.completedTourHashes.includes(this.tourHash)) {
            return;
        }
        try {
            this.completedTourHashes.push(this.tourHash);
            window.localStorage.setItem(localStorageNameCompleted, JSON.stringify(this.completedTourHashes));
        } catch {
            return;
        }
    };

    private removeOverlay = () => {
        if (this.overlayElement !== null) {
            this.overlayElement.remove();
            this.overlayElement = null;
        }
    };

    private showStep = (index: number) => {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data) && this.data?.steps[index] !== undefined) {
            if (this.currentStep !== this.data?.steps[index]) {
                this.uxGuidedTourChange.emit({
                    prevStep: this.currentStep || null,
                    nextStep: this.data?.steps[index] ?? { target: "", targetOffset: 0, title: "" }
                });
            }
            this.currentStepIndex = index;
            this.currentStep = this.data?.steps[index] as GuidedTourStep;
        } else {
            this.deactivate();
        }
    };

    private showIntro = () => {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data) && isDefined(this.data?.intro)) {
            if (this.currentStep !== this.data?.intro) {
                this.uxGuidedTourChange.emit({
                    prevStep: this.currentStep || null,
                    nextStep: this.data?.intro as GuidedTourStep
                });
            }
            this.currentStepIndex = -1;
            if (this.data?.intro) {
                this.currentStep = this.data.intro;
            }
        }
    };

    private showOutro = () => {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data) && isDefined(this.data?.outro)) {
            if (this.currentStep !== this.data?.outro) {
                this.uxGuidedTourChange.emit({
                    prevStep: this.currentStep || null,
                    nextStep: this.data?.outro as GuidedTourStep
                });
            }
            this.currentStepIndex = -1;
            if (this.data?.outro) {
                this.currentStep = this.data.outro;
            }
        }
    };

    private scrollToTarget = (): boolean => {
        const { viewportFixed, targetRect } = this;
        if (targetRect === null || viewportFixed) {
            return true;
        }
        const verticalCenter = this.targetRect ? Math.round(this.targetRect.top + 0.5 * this.targetRect.height) : 0;
        let scrollOffset = 0;
        if (targetRect.top < 0) {
            scrollOffset = verticalCenter - 0.5 * window.innerHeight;
        } else if (targetRect.top + targetRect.height > window.innerHeight) {
            scrollOffset = verticalCenter + 0.5 * window.innerHeight;
        }
        if (scrollOffset === 0) {
            this.viewportFixed = true;
            return true;
        }
        window.scrollBy(0, scrollOffset);
        this.viewportFixed = true;
        return false;
    };

    private showCurrentStep = () => {
        const { currentStep, viewportChanging } = this;
        if (currentStep === null || viewportChanging) {
            this.targetRect = null;
            return;
        }
        const el: HTMLElement | null =
            typeof currentStep.target === "string" ? document.querySelector<HTMLElement>(currentStep.target) : null;
        this.targetRect = el !== null ? el.getBoundingClientRect() : null;
        if (!this.scrollToTarget()) {
            this.showCurrentStepDebounced();
            return;
        }
        if (this.overlayElement === null) {
            const overlay: HTMLUxGuidedTourOverlayElement = document.createElement("ux-guided-tour-overlay");
            overlay.addEventListener("click", this.handleClickOverlay);
            document.body.appendChild(overlay);
            this.overlayElement = overlay;
        }
        this.overlayElement.tourHash = this.tourHash;
        this.overlayElement.targetRect = this.targetRect;
        if (this.currentStep !== null) {
            this.overlayElement.step = this.currentStep;
        }
        this.overlayElement.stepIndex = this.currentStepIndex;
        this.overlayElement.totalSteps = this.totalSteps;
        if (this.overlayElement !== null) {
            this.overlayElement.autoStartSwitch = (this.data?.autoStart ?? false) && this.currentStep === this.data?.intro;
        }
        this.overlayElement.autoStartLabel = this.data?.autoStartLabel ?? "";
        this.overlayElement.finishTourButtonLabel = this.data?.finishTourButtonLabel;
    };

    private showCurrentStepDebounced = debounce(() => {
        this.viewportChanging = false;
        if (this.currentStep !== null) {
            this.showCurrentStep();
        }
    }, 100);

    /** Handle document click events that should activate the guided-tour */
    private handleClickDoc = (event: Event) => {
        const triggerElement = (event.target as HTMLElement).closest(`[${triggerAttributeName}=start]`) as HTMLElement;
        if (triggerElement) {
            this.activate();
        }
    };

    /** Handle overlay click events that should control the guided-tour */
    private handleClickOverlay = (event: Event) => {
        const triggerElement = (event.target as HTMLElement).closest(`[${triggerAttributeName}]`) as HTMLElement;
        if (triggerElement === null) {
            return;
        }
        const action: string = triggerElement.getAttribute(triggerAttributeName) || "";
        switch (action) {
            case "next":
                this.nextStep();
                break;
            case "prev":
                this.prevStep();
                break;
            case "stop":
                this.deactivate();
                this.onCancel();
                break;
            case "finish":
                this.deactivate();
                this.finish();
                break;
            default:
                break;
        }
    };
}

const generateHash = (s: string) =>
    s.split("").reduce((a, b) => {
        // eslint-disable-next-line no-bitwise
        a = (a << 5) - a + b.charCodeAt(0);
        // eslint-disable-next-line no-bitwise
        return a & a;
    }, 0);
